import { useState, useEffect, useMemo } from 'react';
import { Users } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { leadService, userService } from '../../services/api';
import { Modal, Skeleton, EmptyState, Input, Button, Toast, Badge } from '../../components/ui';

const AssignLeadModal = ({ open, leadIds, currentCounsellorId, leadCourseId, onClose, onAssigned }) => {
  const { dark } = useTheme();
  const [counsellors, setCounsellors] = useState([]);
  const [selected, setSelected] = useState('');
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const isBulk = (leadIds?.length || 0) > 1;

  useEffect(() => {
    if (!open) return;
    setSelected(currentCounsellorId || '');
    setRemarks('');
    setToast(null);
    setLoading(true);
    userService.getCounsellors()
      .then(({ data }) => setCounsellors(data.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [open, currentCounsellorId]);

  const sorted = useMemo(() => {
    if (!leadCourseId) return counsellors;
    return [...counsellors].sort((a, b) => {
      const aM = (a.assignedCourses || []).some(c => (c._id || c) === leadCourseId) ? 0 : 1;
      const bM = (b.assignedCourses || []).some(c => (c._id || c) === leadCourseId) ? 0 : 1;
      return aM - bM;
    });
  }, [counsellors, leadCourseId]);

  const handleAssign = async () => {
    if (!selected || !leadIds?.length) return;
    setSubmitting(true);
    try {
      if (isBulk) {
        await leadService.bulkAssign({ leadIds, counsellorId: selected, remarks });
      } else {
        await leadService.assign(leadIds[0], { counsellorId: selected, remarks });
      }
      if (onAssigned) onAssigned();
      onClose();
    } catch (err) {
      setToast({ msg: err.response?.data?.message || err.message || 'Failed to assign lead', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={isBulk ? `Assign ${leadIds?.length || 0} Leads` : 'Assign / Reassign Lead'} width={480}>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
      {loading ? <Skeleton rows={4} cols={1} /> : (
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: dark ? '#94A3B8' : '#64748B', marginBottom: 8 }}>Select Counsellor</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 300, overflowY: 'auto', marginBottom: 14 }}>
            {sorted.length === 0 && <EmptyState icon={Users} title="No counsellors available" description="No counsellors found" />}
            {sorted.map(c => {
              const matches = leadCourseId && (c.assignedCourses || []).some(x => (x._id || x) === leadCourseId);
              const isCurrent = c._id === currentCounsellorId;
              const isSelected = selected === c._id;
              return (
                <label key={c._id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                  borderRadius: 10, cursor: 'pointer',
                  border: `1px solid ${isSelected ? '#3B82F6' : (dark ? '#334155' : '#E2E8F0')}`,
                  background: isSelected ? (dark ? '#3B82F61A' : '#EFF6FF') : 'transparent',
                  transition: 'all 0.15s',
                }}>
                  <input type="radio" name="assign-counsellor" checked={isSelected} onChange={() => setSelected(c._id)} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: dark ? '#F1F5F9' : '#0F172A' }}>{c.firstName} {c.lastName}</div>
                    <div style={{ fontSize: 11, color: dark ? '#64748B' : '#94A3B8', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.email}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    {isCurrent && <Badge color="#6B7280" variant="outline">Current</Badge>}
                    {matches && <Badge color="#22C55E">Handles course</Badge>}
                  </div>
                </label>
              );
            })}
          </div>
          <Input label="Remarks (optional)" value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Reason for this (re)assignment..." />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button onClick={handleAssign} disabled={!selected || submitting}>
              {submitting ? 'Assigning...' : (isBulk ? 'Assign All' : 'Assign')}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default AssignLeadModal;
